import { useEffect, useRef, useState } from 'react'

/**
 * Whether a KeyboardEvent originates from a focusable text element
 * where typed characters should NOT be intercepted (number inputs,
 * text inputs, textareas, contenteditable hosts). Mirrors the check
 * in `useKeyboardShortcuts` so the two systems treat focus the same
 * way. The `allowInInputs` flag bypasses this for truly-global
 * hotkeys like Shift+M — a reveal gesture holds even while the user
 * has a BPM field focused.
 */
function isInputTarget(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null
  const tag = target?.tagName?.toLowerCase()
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    Boolean(target?.isContentEditable)
  )
}

interface KeyComboOpts {
  /**
   * Logical key character — case-insensitive. Examples: `"m"`, `"r"`,
   * `"Escape"`. Matched against `KeyboardEvent.key`.
   */
  key: string
  shift?: boolean
  ctrl?: boolean
  alt?: boolean
  meta?: boolean
  /**
   * If true, fires even when a text input / textarea has focus.
   * Use sparingly — only for visual reveal gestures (Shift+M) that
   * don't interfere with typing. Leave false for action hotkeys
   * (R for record) so typing "r" in a BPM field isn't hijacked.
   */
  allowInInputs?: boolean
}

function matches(e: KeyboardEvent, opts: KeyComboOpts): boolean {
  if (e.key.toLowerCase() !== opts.key.toLowerCase()) return false
  if (opts.shift !== undefined && e.shiftKey !== opts.shift) return false
  if (opts.ctrl !== undefined && e.ctrlKey !== opts.ctrl) return false
  if (opts.alt !== undefined && e.altKey !== opts.alt) return false
  if (opts.meta !== undefined && e.metaKey !== opts.meta) return false
  return true
}

/**
 * Returns `true` while the given key combo is physically held down.
 * Safe to call from any component; each caller gets independent
 * tracking. Used by `MappedControlBadges` so the reveal overlay
 * appears on Shift+M press and disappears on release.
 *
 * Repeated `keydown` events (OS auto-repeat) don't flicker the
 * return value — `keydown` only sets it true, `keyup` sets it false.
 * Window blur resets the flag too, since focus changes can eat the
 * `keyup` event (common when a hotkey opens a system dialog).
 */
export function useHoldKey(combo: KeyComboOpts): boolean {
  const [held, setHeld] = useState(false)
  const comboRef = useRef(combo)
  useEffect(() => {
    comboRef.current = combo
  })

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      const c = comboRef.current
      if (!c.allowInInputs && isInputTarget(e)) return
      if (!matches(e, c)) return
      setHeld(true)
    }
    const onUp = (e: KeyboardEvent) => {
      // For releases we don't gate on isInputTarget — once down, we
      // must honor up even if focus moved into a field mid-hold.
      const c = comboRef.current
      if (e.key.toLowerCase() !== c.key.toLowerCase()) return
      setHeld(false)
    }
    const onBlur = () => setHeld(false)
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  return held
}

/**
 * Fire `handler` once on keydown for the given combo. No repeat
 * firing while held — each physical press equals one call. Useful
 * for toggles / one-shot actions (Record arm, Space for play, etc.).
 */
export function useKeyPress(
  combo: KeyComboOpts,
  handler: (e: KeyboardEvent) => void,
): void {
  const comboRef = useRef(combo)
  const handlerRef = useRef(handler)
  useEffect(() => {
    comboRef.current = combo
    handlerRef.current = handler
  })
  const pressedRef = useRef(false)

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      const c = comboRef.current
      if (!c.allowInInputs && isInputTarget(e)) return
      if (!matches(e, c)) return
      if (pressedRef.current) return // suppress OS auto-repeat
      pressedRef.current = true
      handlerRef.current(e)
    }
    const onUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== comboRef.current.key.toLowerCase()) return
      pressedRef.current = false
    }
    const onBlur = () => {
      pressedRef.current = false
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])
}
